import { api } from '../../../helpers'
import { ISkillsStandards, ISkillsStandardsInfo, updateData, ILvl4Item } from '../components/ProjectSkillsStandards/interfaces'

const step3Paths = {
  getSkillsStandards: (projectID: string) => `/api/projects/${projectID}/skills_and_contents`,
  getInfo: (projectID: string, activeLvl2ID: string) =>
    `/api/projects/${projectID}/developed_students_and_ongoing_projects/${activeLvl2ID}`,
  getInfoSearch: (projectID: string) =>
    `/api/projects/${projectID}/developed_students_and_ongoing_projects_by_lv4_items`,
  updateSkillsStandards: (projectID: string) => `/api/projects/${projectID}/update_skills_and_contents`,
  getSkillsStandardsByIds: (ids: string[]) => `/api/skills_and_contents/by_ids`,
}

// Cache helper com TTL de 1 dia
const CACHE_KEY_PREFIX = 'skills_standards_cache_'
const CACHE_TTL = 24 * 60 * 60 * 1000 // 1 dia em milissegundos

interface CacheEntry<T> {
  data: T
  timestamp: number
}

const getCachedData = <T,>(key: string): T | null => {
  try {
    const cached = localStorage.getItem(key)
    if (!cached) return null

    const entry: CacheEntry<T> = JSON.parse(cached)
    const now = Date.now()

    // Verificar se o cache expirou
    if (now - entry.timestamp > CACHE_TTL) {
      localStorage.removeItem(key)
      return null
    }

    return entry.data
  } catch (error) {
    console.error('Error reading cache:', error)
    return null
  }
}

const setCachedData = <T,>(key: string, data: T): void => {
  try {
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now()
    }
    localStorage.setItem(key, JSON.stringify(entry))
  } catch (error) {
    console.error('Error setting cache:', error)
  }
}

export const getSkillsStandards = async (projectID: string): Promise<ISkillsStandards> => {
  const cacheKey = `${CACHE_KEY_PREFIX}${projectID}`

  // Tentar obter do cache primeiro
  const cachedData = getCachedData<ISkillsStandards>(cacheKey)
  if (cachedData) {
    console.log('📦 [Cache] Using cached skills_and_contents for project', projectID)
    return cachedData
  }

  // Se não tiver no cache, fazer request
  console.log('🌐 [API] Fetching skills_and_contents for project', projectID)
  const response = await api.get<ISkillsStandards>(step3Paths.getSkillsStandards(projectID))

  // Armazenar no cache
  setCachedData(cacheKey, response.data)

  return response.data
}

export const getInfo = async (projectID: string, activeLvl2ID: string): Promise<ISkillsStandardsInfo[]> => {
  const response = await api.get<ISkillsStandardsInfo[]>(step3Paths.getInfo(projectID, activeLvl2ID))
  return response.data
}

export const getInfoSearch = async (projectID: string, lvl4IDs: string[]): Promise<ISkillsStandardsInfo[]> => {
  const response = await api.post<ISkillsStandardsInfo[]>(step3Paths.getInfoSearch(projectID), {
    lv4_item_ids: lvl4IDs,
  })
  return response.data

}

const invalidateCache = (projectID: string): void => {
  try {
    const cacheKey = `${CACHE_KEY_PREFIX}${projectID}`
    localStorage.removeItem(cacheKey)
    console.log('🗑️ [Cache] Invalidated cache for project', projectID)
  } catch (error) {
    console.error('Error invalidating cache:', error)
  }
}

export const updateSelectedSkillsStandards = async (projectID: string, data: updateData) => {
  const response = await api.patch(step3Paths.updateSkillsStandards(projectID), data)

  // Invalidar cache após atualização
  invalidateCache(projectID)

  return response.data
}

export const getSkillsStandardsByIds = async (ids: string[]): Promise<ILvl4Item[]> => {
  try {
    const response = await api.post<ILvl4Item[]>(step3Paths.getSkillsStandardsByIds(ids), {
      ids: ids
    })
    return response.data
  } catch (error) {
    console.error('Error fetching skills/standards by IDs:', error)
    return []
  }
}
